/**
 * HTML-Dokument → PDF herunterladen / teilen.
 *
 * KEIN html2canvas — der crasht mobil mit Range.setEnd.
 * Stattdessen: DOM messen → SVG foreignObject → Canvas → jsPDF.
 *
 * scrubMode:
 *  - 'full' (Default, PE Papers): Tailwind-Klassen entfernen, Capture-CSS setzen
 *  - 'minimal': Klassen behalten (für Dokumente mit eigenem Print-CSS, z. B. Leistungslohn)
 */

import jsPDF from 'jspdf';

const CAPTURE_WIDTH_PX = 794;
const A4_PAGE_HEIGHT_PX = Math.round((CAPTURE_WIDTH_PX * 297) / 210);
/** ~11 mm Rand — mehr Nutzfläche, damit lesbare Schrift nicht runterskaliert wird */
const CAPTURE_PAD_Y = 42;
const CAPTURE_PAD_X = 40;

const PRESERVE_CLASSES = new Set([
    'print-break',
    'pepapers-print-break',
    'print-signatures-grid',
    'signature-col',
    'fill-line',
    'fill-line-sm',
    'sig-line',
    'optional-box',
    'optional-badge',
    'doc-panel',
    'doc-kicker',
    'pdf-letterhead',
    'av-a4-page',
    'av-letter-page',
    'av-letter-closing',
    'av-doc-section',
    'av-meta-grid',
    'av-meta-col',
    'av-meta-label',
    'av-meta-date',
    'av-doc-p',
    'av-table-wrap',
    'av-ll-page',
    'av-sig-grid',
    'ab-sig-table',
    'ab-sig-line-soft',
]);

const PAGE_LETTERHEAD_HTML = `
<div class="pdf-letterhead">
  <h1>PreisExpert</h1>
  <h2>Das Betriebssystem für erfolgreiche Friseure</h2>
</div>
`;

export type HtmlDocumentPdfOptions = {
    html: string;
    filename: string;
    extraCss?: string;
    /** 'full' = PE-Papers-Scrub; 'minimal' = Print-CSS behalten (Leistungslohn). */
    scrubMode?: 'full' | 'minimal';
    /** Abschnitte an .print-break getrennt rendern (wie Druck). Default: true. */
    splitPrintBreaks?: boolean;
    /**
     * Sektion möglichst auf 1 A4 bringen — nur leichte Skalierung (≥90 %).
     * Zu langer Inhalt wird auf weitere Seiten geschnitten (lesbare Schrift).
     */
    fitSectionsToPage?: boolean;
    /** PreisExpert-Letterhead auf Folgeseiten. Default: true. */
    letterheadOnBreaks?: boolean;
    /**
     * Extra Capture-Rand in px (außen um die Sektion).
     * Bei Dokumenten mit eigenem Seiten-Padding (z. B. Arbeitsvertrag av-a4-page) auf 0 setzen,
     * sonst Doppelrand und „gequetschter“ Inhaltsblock.
     */
    capturePadPx?: number;
};

function sanitizeFilename(name: string): string {
    const raw = String(name || 'Dokument.pdf').trim() || 'Dokument.pdf';
    const withExt = /\.pdf$/i.test(raw) ? raw : `${raw}.pdf`;
    return withExt.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_').slice(0, 120);
}

function isLikelyMobile(): boolean {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
    if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) return true;
    return false;
}

function writeLoadingDoc(win: Window, title: string): void {
    try {
        win.document.open();
        win.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><title>${title}</title>
<style>body{margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;background:#0f172a;color:#e2e8f0;padding:24px;text-align:center}</style>
</head><body><p>PDF wird erstellt…<br/><span style="opacity:.7;font-size:12px">Bitte kurz warten.</span></p></body></html>`);
        win.document.close();
    } catch {
        /* ignore */
    }
}

function writePdfViewer(win: Window, blobUrl: string, filename: string): void {
    const titleSafe = filename.replace(/</g, '').replace(/>/g, '').replace(/"/g, '');
    try {
        win.document.open();
        win.document.write(`<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${titleSafe}</title>
  <style>
    html, body { margin: 0; height: 100%; background: #0f172a; font-family: system-ui, sans-serif; }
    .bar {
      display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
      min-height: 52px; padding: 10px 14px; box-sizing: border-box;
      background: #1e293b; color: #e2e8f0; border-bottom: 1px solid #334155;
    }
    .bar span { flex: 1; min-width: 120px; font-size: 12px; opacity: 0.85; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .bar a {
      appearance: none; border: 1px solid #f59e0b; background: #f59e0b; color: #0f172a;
      border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 700;
      text-decoration: none; cursor: pointer;
    }
    iframe { border: 0; width: 100%; height: calc(100% - 72px); background: #fff; }
  </style>
</head>
<body>
  <div class="bar">
    <span title="${titleSafe}">${titleSafe}</span>
    <a href="${blobUrl}" download="${titleSafe}">PDF speichern</a>
  </div>
  <iframe title="PDF-Vorschau" src="${blobUrl}"></iframe>
</body>
</html>`);
        win.document.close();
    } catch {
        try {
            win.location.href = blobUrl;
        } catch {
            /* ignore */
        }
    }
}

async function deliverPdfBlob(
    blob: Blob,
    filename: string,
    preOpenedWin: Window | null
): Promise<void> {
    const url = URL.createObjectURL(blob);

    // Web Share erst versuchen — Fenster NICHT vorher schließen.
    // Nach async PDF-Erstellung scheitert Share auf iOS oft (NotAllowedError);
    // dann muss die vorgeöffnete Vorschau noch da sein.
    if (isLikelyMobile() && typeof navigator.share === 'function' && typeof File !== 'undefined') {
        try {
            const file = new File([blob], filename, { type: 'application/pdf' });
            const canShare =
                typeof navigator.canShare !== 'function' || navigator.canShare({ files: [file] });
            if (canShare) {
                await navigator.share({ files: [file], title: filename });
                try {
                    preOpenedWin?.close();
                } catch {
                    /* ignore */
                }
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
                return;
            }
        } catch (err: any) {
            // AbortError = Nutzer hat Share abgebrochen → trotzdem Vorschau anbieten
            // NotAllowedError / andere = Share nach async nicht erlaubt (typisch iOS)
        }
    }

    if (preOpenedWin && !preOpenedWin.closed) {
        writePdfViewer(preOpenedWin, url, filename);
        window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
        return;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();

    // iOS blockiert window.open nach async oft — nur versuchen wenn kein Share/Viewer
    if (isLikelyMobile()) {
        try {
            const opened = window.open(url, '_blank');
            if (!opened) {
                // Letzter Ausweg: gleiche Seite navigieren (selten)
                window.location.assign(url);
            }
        } catch {
            window.location.assign(url);
        }
    }

    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function scrubSoftHyphens(root: HTMLElement): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let n = walker.nextNode();
    while (n) {
        nodes.push(n as Text);
        n = walker.nextNode();
    }
    for (const node of nodes) {
        node.textContent = String(node.textContent || '')
            .replace(/\u00ad/g, '')
            .replace(/[\u200B-\u200D\uFEFF]/g, '')
            .replace(/\u00a0/g, ' ');
    }
}

function scrubFull(root: HTMLElement): void {
    scrubSoftHyphens(root);

    root.querySelectorAll('.hidden').forEach((el) => {
        el.parentNode?.removeChild(el);
    });

    root.querySelectorAll('*').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const kept = String(el.className || '')
            .split(/\s+/)
            .filter((c) => PRESERVE_CLASSES.has(c));
        if (kept.length) el.className = kept.join(' ');
        else el.removeAttribute('class');
        el.removeAttribute('style');
    });
    root.normalize();
}

function scrubMinimal(root: HTMLElement): void {
    scrubSoftHyphens(root);
    // Nur screen-hidden entfernen; Print-CSS-Klassen bleiben.
    root.querySelectorAll('.hidden').forEach((el) => {
        el.parentNode?.removeChild(el);
    });
    // Inline-Styles von React/Tailwind-Animationen stören foreignObject selten — belassen.
    root.normalize();
}

/** foreignObject lädt keine relativen/externen Bilder → als data:-URL einbetten. */
async function inlineImagesAsDataUrls(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
        imgs.map(async (img) => {
            const src = img.getAttribute('src') || '';
            if (!src || src.startsWith('data:')) return;
            try {
                const abs = new URL(src, window.location.href).href;
                const res = await fetch(abs, { credentials: 'same-origin' });
                if (!res.ok) return;
                const blob = await res.blob();
                const dataUrl = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || ''));
                    reader.onerror = () => reject(new Error('Logo konnte nicht gelesen werden.'));
                    reader.readAsDataURL(blob);
                });
                if (dataUrl) img.setAttribute('src', dataUrl);
            } catch {
                /* Bild bleibt unersetzt — PDF zeigt ggf. Platzhalter */
            }
        })
    );
}

const CAPTURE_CSS = `
  * { box-sizing: border-box; letter-spacing: normal !important; word-spacing: normal !important; }
  body, div, p, span, strong, em, h1, h2, h3, h4 {
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
  }
  h1 {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 4px;
    text-align: center;
  }
  h2 {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #4b5563;
    margin: 0 0 14px;
    text-align: center;
  }
  h3 {
    font-size: 14px;
    font-weight: bold;
    margin: 0 0 10px;
    text-transform: uppercase;
    border: 1px solid #d1d5db;
    background: #f3f4f6;
    padding: 10px 12px;
    text-align: center;
  }
  h4 {
    font-size: 14px;
    font-weight: bold;
    margin: 16px 0 8px;
    border-bottom: 1px solid #d1d5db;
    padding-bottom: 4px;
  }
  p { margin: 0 0 11px; text-align: justify !important; line-height: 1.55; font-size: 13px; }
  strong { font-weight: bold; }
  .pdf-letterhead {
    text-align: center;
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
    margin: 0 0 18px;
  }
  .pdf-letterhead h1 { margin-bottom: 2px; }
  .pdf-letterhead h2 { margin-bottom: 0; color: #4b5563; }
  .doc-kicker {
    text-align: center !important;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #6b7280;
    margin: 0 0 16px;
  }
  .doc-panel {
    border: 1.5px solid #1f2937;
    background: #fafafa;
    padding: 20px 18px;
    margin: 4px 0 8px;
  }
  .print-break, .pepapers-print-break {
    margin-top: 0 !important;
    padding-top: 0 !important;
    page-break-before: auto !important;
  }
  .print-signatures-grid { display: table !important; width: 100% !important; margin-top: 36px; }
  .signature-col { display: table-cell !important; width: 50% !important; padding-right: 24px; vertical-align: top; }
  .fill-line {
    display: inline-block;
    border-bottom: 1px solid #000;
    width: 12rem;
    margin: 0 0.5rem;
    min-height: 1em;
    vertical-align: bottom;
  }
  .fill-line-sm {
    display: inline-block;
    border-bottom: 1px solid #000;
    width: 3rem;
    margin: 0 0.25rem;
    min-height: 1em;
    vertical-align: bottom;
  }
  .sig-line {
    border-bottom: 1px solid #000;
    height: 2rem;
    margin-bottom: 0.35rem;
  }
  .optional-box {
    border: 1px dashed #6b7280;
    padding: 14px 14px 10px;
    margin: 14px 0;
    position: relative;
    background: #fff;
  }
  .optional-badge {
    position: absolute;
    top: -9px;
    left: 12px;
    background: #fff;
    padding: 0 6px;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
  }
`;

function splitHtmlByPrintBreak(html: string, letterheadOnBreaks = true): string[] {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    const breakEls = Array.from(
        wrap.querySelectorAll<HTMLElement>('.print-break, .pepapers-print-break')
    );
    if (!breakEls.length) return [html];

    const sections: string[] = [];
    const page1 = document.createElement('div');

    Array.from(wrap.childNodes).forEach((node) => {
        if (
            node instanceof HTMLElement &&
            (node.classList.contains('print-break') || node.classList.contains('pepapers-print-break'))
        ) {
            return;
        }
        page1.appendChild(node.cloneNode(true));
    });

    const page1Html = page1.innerHTML.trim();
    if (page1Html) sections.push(page1Html);

    breakEls.forEach((el) => {
        const prefix = letterheadOnBreaks ? PAGE_LETTERHEAD_HTML : '';
        // outerHTML behält Layout-Klassen (z. B. av-a4-page / av-ll-page) — innerHTML zerstört das Design
        const clone = el.cloneNode(true) as HTMLElement;
        clone.classList.remove('print-break', 'pepapers-print-break');
        sections.push(`${prefix}${clone.outerHTML}`);
    });

    return sections.length ? sections : [html];
}

async function prepareMeasureNode(
    html: string,
    extraCss: string | undefined,
    scrubMode: 'full' | 'minimal',
    padX: number,
    padY: number
): Promise<{ host: HTMLElement; width: number; height: number; xhtmlInner: string }> {
    const host = document.createElement('div');
    host.setAttribute('data-pepapers-pdf-measure', 'true');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = [
        'position:fixed',
        'left:-10000px',
        'top:0',
        `width:${CAPTURE_WIDTH_PX}px`,
        'height:auto',
        'min-height:0',
        'background:#ffffff',
        'color:#000000',
        'pointer-events:none',
        'z-index:-1',
        `padding:${padY}px ${padX}px`,
        'box-sizing:border-box',
        'overflow:visible',
    ].join(';');

    const style = document.createElement('style');
    // Extra-CSS MUSS nach CAPTURE_CSS kommen, sonst überschreiben die generischen h3/p-Regeln das Layout
    style.textContent = `${CAPTURE_CSS}\n${extraCss || ''}`;

    const content = document.createElement('div');
    content.style.cssText = 'width:100%;height:auto;min-height:0;';
    content.innerHTML = html;
    if (scrubMode === 'minimal') scrubMinimal(content);
    else scrubFull(content);

    await inlineImagesAsDataUrls(content);

    host.appendChild(style);
    host.appendChild(content);
    document.body.appendChild(host);

    void host.offsetHeight;

    const width = CAPTURE_WIDTH_PX;
    const measured = Math.ceil(
        Math.max(content.scrollHeight, content.offsetHeight, 1) + padY * 2
    );
    const height = Math.max(Math.min(measured, A4_PAGE_HEIGHT_PX * 12), 400);

    const serializer = new XMLSerializer();
    let xhtmlInner = serializer.serializeToString(content);
    if (!xhtmlInner.includes('xmlns=')) {
        xhtmlInner = xhtmlInner.replace(
            /^<([a-zA-Z0-9]+)/,
            '<$1 xmlns="http://www.w3.org/1999/xhtml"'
        );
    }

    return { host, width, height, xhtmlInner };
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('PDF-Grafik konnte nicht geladen werden.'));
        img.src = url;
    });
}

async function htmlToCanvasViaSvg(
    html: string,
    extraCss: string | undefined,
    scrubMode: 'full' | 'minimal',
    padX: number,
    padY: number
): Promise<HTMLCanvasElement> {
    const { host, width, height, xhtmlInner } = await prepareMeasureNode(
        html,
        extraCss,
        scrubMode,
        padX,
        padY
    );

    try {
        // Dokument-CSS zuletzt → schlägt CAPTURE_CSS (sonst Seite 2 = Generik-Kasten)
        const cssBlock = `${CAPTURE_CSS}\n${extraCss || ''}`
            .replace(/]]>/g, ']] >')
            .replace(/&/g, '&amp;');

        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
  <foreignObject width="100%" height="100%">
    <div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;height:${height}px;overflow:hidden;background:#ffffff;color:#000000;padding:${padY}px ${padX}px;box-sizing:border-box;font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.55;">
      <style>${cssBlock}</style>
      ${xhtmlInner}
    </div>
  </foreignObject>
</svg>`;

        const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        const img = await loadImage(svgUrl);

        const scale = isLikelyMobile() ? 1.5 : 2;
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(width * scale);
        canvas.height = Math.ceil(height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas nicht verfügbar.');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas;
    } finally {
        if (host.parentNode) host.parentNode.removeChild(host);
    }
}

/** Kurze Abschnitte auf volle A4-Höhe mit Weißraum auffüllen (saubere Seiten). */
function padCanvasToFullPage(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const scale = canvas.width / CAPTURE_WIDTH_PX;
    const targetH = Math.ceil(A4_PAGE_HEIGHT_PX * scale);
    if (canvas.height >= targetH - 2) return canvas;

    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = targetH;
    const ctx = out.getContext('2d');
    if (!ctx) return canvas;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);
    return out;
}

/**
 * Leichte Einpassung auf 1 A4 — aber nie unter MIN_FIT_SCALE stauchen.
 * Zu langer Inhalt → null (dann Multipage-Schnitt mit lesbarer Schrift).
 */
const MIN_FIT_SCALE = 0.9;

function tryFitCanvasToSingleA4Page(canvas: HTMLCanvasElement): HTMLCanvasElement | null {
    const scaleX = canvas.width / CAPTURE_WIDTH_PX;
    const targetH = Math.ceil(A4_PAGE_HEIGHT_PX * scaleX);
    const targetW = canvas.width;

    if (canvas.height <= targetH * 1.02) {
        return padCanvasToFullPage(canvas);
    }

    const neededScale = targetH / canvas.height;
    if (neededScale < MIN_FIT_SCALE) {
        // Lieber 2 lesbare Seiten als unleserlich gequetschte Schrift
        return null;
    }

    const out = document.createElement('canvas');
    out.width = targetW;
    out.height = targetH;
    const ctx = out.getContext('2d');
    if (!ctx) return canvas;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    const drawH = canvas.height * neededScale;
    const drawW = canvas.width * neededScale;
    const offsetX = (targetW - drawW) / 2;
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, offsetX, 0, drawW, drawH);
    return out;
}

function appendCanvasToPdf(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    mobile: boolean,
    addPageBefore: boolean
): void {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const pxPerMm = canvas.width / imgWidth;
    const pageHeightPx = Math.max(1, Math.floor(pageHeight * pxPerMm));
    const quality = mobile ? 0.86 : 0.93;

    let y = 0;
    let pageIndex = 0;
    const maxPages = 20;

    while (y < canvas.height - 1 && pageIndex < maxPages) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - y);
        if (pageIndex > 0 && sliceHeight < pageHeightPx * 0.08) break;

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext('2d');
        if (!ctx) throw new Error('Canvas nicht verfügbar.');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width;
        const imgData = sliceCanvas.toDataURL('image/jpeg', quality);

        if (addPageBefore || pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, sliceImgHeight);

        y += sliceHeight;
        pageIndex += 1;
    }
}

function appendSinglePageCanvasToPdf(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    mobile: boolean,
    addPageBefore: boolean
): void {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const quality = mobile ? 0.86 : 0.93;
    const imgData = canvas.toDataURL('image/jpeg', quality);
    if (addPageBefore) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
}

async function htmlToPdfBlob(options: HtmlDocumentPdfOptions, mobile: boolean): Promise<Blob> {
    const scrubMode = options.scrubMode || 'full';
    const doSplit = options.splitPrintBreaks !== false;
    const fitToPage = !!options.fitSectionsToPage;
    const letterheadOnBreaks = options.letterheadOnBreaks !== false;
    const padRaw = options.capturePadPx;
    const padX = typeof padRaw === 'number' ? Math.max(0, padRaw) : CAPTURE_PAD_X;
    const padY = typeof padRaw === 'number' ? Math.max(0, padRaw) : CAPTURE_PAD_Y;
    const sections = doSplit ? splitHtmlByPrintBreak(options.html, letterheadOnBreaks) : [options.html];
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < sections.length; i++) {
        let canvas = await htmlToCanvasViaSvg(sections[i], options.extraCss, scrubMode, padX, padY);
        if (!canvas.width || !canvas.height) {
            throw new Error('PDF-Vorschau konnte nicht gerendert werden.');
        }

        if (fitToPage) {
            const fitted = tryFitCanvasToSingleA4Page(canvas);
            if (fitted) {
                appendSinglePageCanvasToPdf(pdf, fitted, mobile, i > 0);
                continue;
            }
            // Overflow: normale Mehrseiten-Ausgabe (lesbar)
        }

        // Ein Abschnitt der auf eine Seite passt → volle A4-Seite
        const scale = canvas.width / CAPTURE_WIDTH_PX;
        if (canvas.height <= A4_PAGE_HEIGHT_PX * scale * 1.02) {
            canvas = padCanvasToFullPage(canvas);
        }
        appendCanvasToPdf(pdf, canvas, mobile, i > 0);
    }

    return pdf.output('blob');
}

/**
 * Rendert HTML → A4-PDF-Blob (ohne Download) — z. B. für Druck = 1:1 wie PDF-Export.
 */
export async function buildHtmlAsPdfBlob(options: HtmlDocumentPdfOptions): Promise<Blob> {
    const html = String(options.html || '').trim();
    if (!html) {
        throw new Error('Kein Dokumentinhalt für den PDF-Export.');
    }
    return htmlToPdfBlob({ ...options, html }, isLikelyMobile());
}

/**
 * Rendert HTML → A4-PDF ohne html2canvas.
 */
export async function downloadHtmlAsPdf(options: HtmlDocumentPdfOptions): Promise<void> {
    const html = String(options.html || '').trim();
    if (!html) {
        throw new Error('Kein Dokumentinhalt für den PDF-Export.');
    }

    const filename = sanitizeFilename(options.filename);
    const mobile = isLikelyMobile();

    let preOpenedWin: Window | null = null;
    if (mobile) {
        preOpenedWin = window.open('about:blank', '_blank');
        if (preOpenedWin) writeLoadingDoc(preOpenedWin, 'PDF wird erstellt…');
    }

    try {
        const blob = await htmlToPdfBlob({ ...options, html }, mobile);
        await deliverPdfBlob(blob, filename, preOpenedWin);
    } catch (err) {
        try {
            preOpenedWin?.close();
        } catch {
            /* ignore */
        }
        throw err;
    }
}
